import React, {useState} from 'react';
import {Navigate, useLocation, useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {AdminAuthProvider, useAdminAuth} from './AdminAuthContext';

const {FiArrowLeft, FiLock} = FiIcons;

export default function AdminGate({children}) {
  return (
    <AdminAuthProvider>
      <AdminAccessGate>{children}</AdminAccessGate>
    </AdminAuthProvider>
  );
}

function AdminAccessGate({children}) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    session,
    authLoading,
    authError,
    setAuthError,
    membership,
    signIn,
    signOut
  } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    const {error} = await signIn(email.trim(), password);
    setSubmitting(false);
    if (!error) navigate('/admin', {replace: true});
  };

  const handleSignOut = async () => {
    const {error} = await signOut();
    if (!error) navigate('/admin-login', {replace: true});
  };

  if (authLoading) return <AuthMessage message="Checking Supabase session…" />;

  if (!session) {
    if (location.pathname !== '/admin-login') {
      return <Navigate to="/admin-login" replace />;
    }

    return (
      <main className="admin-login-shell">
        <form className="admin-login-card" onSubmit={submit}>
          <span className="login-icon"><SafeIcon icon={FiLock} /></span>
          <span className="eyebrow">Private guide area</span>
          <h1>Guide Admin</h1>
          <p>Sign in with your SOSBooking Admin account.</p>
          <label>
            Email
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          <label>
            Password
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          {authError && <div className="alert error">{authError}</div>}
          <button className="button primary full" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          <button
            type="button"
            className="back-link"
            onClick={() => navigate('/book')}
          >
            <SafeIcon icon={FiArrowLeft} /> Back to customer booking
          </button>
        </form>
      </main>
    );
  }

  if (membership.userId !== session.user.id || membership.status === 'loading') {
    return <AuthMessage message="Verifying guide membership…" />;
  }

  if (membership.status !== 'allowed') {
    const message = membership.status === 'error'
      ? 'Unable to verify your guide membership. Admin access is blocked.'
      : 'Your account is not provisioned for exactly one SOSBooking guide.';

    return (
      <main className="admin-login-shell">
        <section className="admin-login-card">
          <span className="eyebrow">Admin access unavailable</span>
          <h1>Access denied</h1>
          <p>{message}</p>
          {authError && <div className="alert error">{authError}</div>}
          <button className="button secondary" onClick={handleSignOut}>
            Sign out
          </button>
        </section>
      </main>
    );
  }

  if (location.pathname === '/admin-login') {
    return <Navigate to="/admin" replace />;
  }

  return children;
}

function AuthMessage({message}) {
  return (
    <main className="admin-login-shell">
      <p>{message}</p>
    </main>
  );
}